import { Component, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import type { IFilterDisplayAngularComp } from 'ag-grid-angular';
import type { FilterDisplayParams, IAfterGuiAttachedParams } from 'ag-grid-community';

import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import styles from './VbColumnFilter.module.less';

const SEARCH_DEBOUNCE_MS: number = 500;

export interface IVbTextColumnFilterParams {
	placeholderText?: string;
	fieldText?: string;
}

export interface IVbTextColumnFilterModel {
	type: string;
	filter: string;
	filterType: string;
}

interface IVbTextColumnFilterMergedParams extends FilterDisplayParams<any, any, { type: string, filter: string, filterType: string }>, IVbTextColumnFilterParams { }

@Component({
	standalone: true,
	imports: [FormsModule, CommonModule, TranslateModule],
	template: `
		<div [ngClass]="styles.wrapper">
			<div [ngClass]="styles.label">{{ filterParams.fieldText }}</div>
			<div [ngClass]="styles.inputWrapper">
				<input
					#eFilterText
					type="text"
					[ngClass]="styles.input"
					[(ngModel)]="columnFilter.filter"
					(ngModelChange)="onInputChanged()"
					[placeholder]="filterParams.placeholderText"
				/>
				<div class="padding-top-10">{{ 'ColumnFilter_ExactMatchOnly' | translate }}</div>
			</div>
		</div>
	`,
})
export class VbTextColumnFilterComponent implements IFilterDisplayAngularComp {
	@ViewChild('eFilterText') public eFilterText!: ElementRef;

	public readonly styles = styles;
	public filterParams: IVbTextColumnFilterMergedParams;
	public columnFilter: IVbTextColumnFilterModel = { type: 'contains', filter: '', filterType: 'text' };
	private readonly inputChanged$ = new Subject<string>();

	constructor() {
		this.inputChanged$.pipe(
			debounceTime(SEARCH_DEBOUNCE_MS)
		).subscribe(value => {
			const model = {
				type: 'contains',
				filter: value || '',
				filterType: 'text'
			};
			this.filterParams.onModelChange(model);
		});
	}

	public agInit(params: IVbTextColumnFilterMergedParams): void {
		this.filterParams = params;
		this.refresh(params);
	}

	public refresh(newParams: IVbTextColumnFilterMergedParams): boolean {
		const currentModel = this.columnFilter;
		const newModel = newParams.model ?? { type: 'contains', filter: '', filterType: 'text' };
		if (JSON.stringify(newModel) !== JSON.stringify(currentModel)) {
			this.columnFilter = { ...newModel };
		}
		return true;
	}

	public onInputChanged() {
		this.inputChanged$.next(this.columnFilter.filter);
	}

	public afterGuiAttached(params?: IAfterGuiAttachedParams): void {
		if (!params?.suppressFocus) {
			// focus the input element for keyboard navigation
			this.eFilterText!.nativeElement.focus();
		}
	}
}
